import { z } from "zod";
import { personSchema } from "./people.js";
import { idSchema } from "./text.js";
// Impostazioni → «Obiettivi» (piano M7, Task 6; D9; SEZ-16; mock-up `report.html` del 17/10/2026): `GET` e `PUT
// /api/settings/report-goals`. Gli obiettivi di vendita del mese persona per persona, il valore medio di una vendita e le percentuali
// con cui il Report colora i numeri e risale dalle vendite alle chiamate. Le legge e le cambia soltanto chi ha `settings.goals` (di
// partenza il Super admin, e il Super admin di Gioia365); il Report mostra l'obiettivo di una persona a chi ne vede i numeri.
//
// Le percentuali decise (disco su chiamate 8–10% e show up delle demo almeno 50%, SEZ-2) non sono nel corpo: mandarle è `422`. Gli
// importi sono in centesimi, fino a 10 milioni di euro; le percentuali da più di 0 a 100 con al più un decimale, «Da» non oltre «A».
// I mesi sono di calendario, `AAAA-MM`, nel fuso dell'azienda; di partenza quello di oggi.
/** Il valore più alto di un importo degli obiettivi: 10 milioni di euro, in centesimi (il `check` del database è lo stesso). */
export const REPORT_GOAL_MAX_CENTS = 1_000_000_000;
/** Quanti obiettivi delle persone un salvataggio porta al più. */
export const REPORT_GOALS_MAX = 200;
/** Un mese di calendario, `AAAA-MM`. */
export const reportMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, { message: "month is YYYY-MM" });
/** Query di `GET /api/settings/report-goals`: il mese, di partenza quello di oggi nel fuso dell'azienda. */
export const reportGoalsQuerySchema = z.strictObject({ month: reportMonthSchema.optional() });
const centsSchema = z.number().int().min(0).max(REPORT_GOAL_MAX_CENTS);
/** Una percentuale degli obiettivi: più di 0, al più 100, con al più un decimale (18,5%). */
const percentSchema = z
    .number()
    .gt(0)
    .max(100)
    .refine((value) => Math.abs(value * 10 - Math.round(value * 10)) < 1e-9, { message: "a percentage has at most one decimal" });
/** Un obiettivo che si cambia: «Da» e, se c'è, «A», non più basso di «Da». */
const editableTargetSchema = z
    .strictObject({ min: percentSchema, max: percentSchema.optional() })
    .refine((target) => target.max === undefined || target.min <= target.max, { message: "min must not be above max" });
/** Le percentuali che il Super admin cambia (`EditableReportTargets` del dominio); le decise non ci sono. */
export const editableReportTargetsSchema = z.strictObject({
    discoOnAnswered: editableTargetSchema,
    discoShow: editableTargetSchema,
    demoOnDisco: editableTargetSchema,
    closeRate: editableTargetSchema,
});
/** Le chiavi delle percentuali che si cambiano, nell'ordine del dominio (`DEFAULT_EDITABLE_REPORT_TARGETS`). */
export const EDITABLE_REPORT_TARGET_KEYS = Object.freeze(Object.keys(editableReportTargetsSchema.shape));
/** Le chiavi delle percentuali decise (SEZ-2), in sola lettura nella pagina: «deciso, non si cambia». */
export const FIXED_REPORT_TARGET_KEYS = Object.freeze(["discoOnCalls", "demoShow"]);
const targetViewSchema = z.object({ min: z.number(), max: z.number().optional() });
export const reportGoalsResponseSchema = z.object({
    month: z.string(),
    /** La versione della riga delle impostazioni del Report: un salvataggio la confronta (`409 version_conflict`). */
    version: z.number().int().min(1),
    canEdit: z.boolean(),
    /** Il valore medio di una vendita, in centesimi; nullo finché non si scrive. */
    averageSaleCents: z.number().int().nullable(),
    /** Tutte le percentuali dei colori, le decise comprese: quelle salvate, o di partenza quelle di «Calcolo Sales». */
    targets: z.object({
        discoOnCalls: targetViewSchema,
        discoOnAnswered: targetViewSchema,
        discoShow: targetViewSchema,
        demoOnDisco: targetViewSchema,
        demoShow: targetViewSchema,
        closeRate: targetViewSchema,
    }),
    /**
     * Una riga per Commerciale: gli owner possibili attivi e chi ha un obiettivo nel mese (anche chi non è più attivo), in ordine
     * alfabetico italiano, con l'obiettivo del mese in centesimi o nullo.
     */
    people: z.array(z.object({ person: personSchema, salesGoalCents: z.number().int().nullable() })),
});
/**
 * Corpo di `PUT /api/settings/report-goals`: il mese degli obiettivi, il valore medio (nullo lo toglie), le quattro percentuali che si
 * cambiano e gli obiettivi delle persone da scrivere in quel mese (nullo toglie quello della persona; le persone che non ci sono
 * restano come sono), una persona una volta sola, e la versione letta.
 */
export const saveReportGoalsBodySchema = z
    .strictObject({
    month: reportMonthSchema,
    averageSaleCents: z.number().int().min(1).max(REPORT_GOAL_MAX_CENTS).nullable(),
    targets: editableReportTargetsSchema,
    goals: z.array(z.strictObject({ membershipId: idSchema, salesGoalCents: centsSchema.nullable() })).max(REPORT_GOALS_MAX),
    expectedVersion: z.number().int().min(1),
})
    .superRefine((body, context) => {
    const ids = body.goals.map((goal) => goal.membershipId);
    if (new Set(ids).size !== ids.length)
        context.addIssue({ code: "custom", path: ["goals"], message: "a person appears once" });
});
//# sourceMappingURL=report-goals.js.map